import type {
  BiomeshContactResponse,
  BiomeshOperationResponse,
  BiomeshUnitResponse,
} from "@phc/common/gen/threatAnalyticsGen"
import {
  useListContactsBiomeshContactListGet,
  useListOperationsBiomeshOperationListGet,
  useListPlatformsBiomeshPlatformListGet,
  useListSensorsBiomeshSensorListGet,
  useListUnitsBiomeshUnitListGet,
} from "@phc/common/gen/threatAnalyticsGen"
import { useMemo } from "react"
import type { BioMeshFilters } from "./BioMeshUIStateContext"
import { groupSensorsByNode } from "./BioMeshUIStateContext"

export function useNodeDrawerData(nodeIds: string[], filters: BioMeshFilters) {
  const isDrawerOpen = nodeIds.length > 0

  const { data: allNodes } = useListPlatformsBiomeshPlatformListGet(
    {
      include: "operations",
    },
    {
      query: { enabled: isDrawerOpen },
    }
  )
  const { data: allSensors } = useListSensorsBiomeshSensorListGet(
    {
      limit: 10_000,
      include: "contacts",
    },
    {
      query: { enabled: isDrawerOpen },
    }
  )
  const { data: allOperations } = useListOperationsBiomeshOperationListGet(
    {
      include: "units,contacts",
    },
    {
      query: { enabled: isDrawerOpen },
    }
  )
  const { data: allUnits } = useListUnitsBiomeshUnitListGet(
    {
      include: "operations,contacts",
    },
    {
      query: { enabled: isDrawerOpen },
    }
  )
  const { data: allContacts } = useListContactsBiomeshContactListGet(
    {
      include: "operations,units",
    },
    {
      query: { enabled: isDrawerOpen },
    }
  )

  const nodeOptions = useMemo(
    () => allNodes?.filter(p => nodeIds.includes(p.platform_id)) ?? [],
    [allNodes, nodeIds]
  )

  const sensorsByNode = useMemo(
    () => groupSensorsByNode(allSensors, filters, nodeIds),
    [allSensors, nodeIds, filters]
  )

  const operationMap = useMemo(() => {
    const map = new Map<string, BiomeshOperationResponse>()
    allOperations?.forEach(o => map.set(o.operation_id, o))
    return map
  }, [allOperations])

  const unitMap = useMemo(() => {
    const map = new Map<string, BiomeshUnitResponse>()
    allUnits?.forEach(u => map.set(u.unit_id, u))
    return map
  }, [allUnits])

  const contactMap = useMemo(() => {
    const map = new Map<string, BiomeshContactResponse>()
    allContacts?.forEach(c => map.set(c.contact_id, c))
    return map
  }, [allContacts])

  return {
    nodeOptions,
    sensorsByNode,
    operationMap,
    unitMap,
    contactMap,
  }
}
